import React from 'react';
import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import {
  ExperimentOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

type AppModeCardsProps = {
  compact?: boolean;
};

type ModeCard = {
  key: string;
  title: string;
  tag: string;
  icon: React.ReactNode;
  description: string;
  primaryLabel: string;
  primaryPath?: string;
  secondaryLabel?: string;
  secondaryPath?: string;
};

const AppModeCards: React.FC<AppModeCardsProps> = ({ compact = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('layout');

  const modes: ModeCard[] = [
    {
      key: 'scenarios',
      title: t('appModes.scenarios.title'),
      tag: t('appModes.scenarios.tag'),
      icon: <ExperimentOutlined />,
      description: t('appModes.scenarios.description'),
      primaryLabel: t('appModes.scenarios.primary'),
      primaryPath: '/scenarios',
      secondaryLabel: t('appModes.scenarios.secondary'),
      secondaryPath: '/saves?type=scenario',
    },
    {
      key: 'free',
      title: t('appModes.free.title'),
      tag: t('appModes.free.tag'),
      icon: <ThunderboltOutlined />,
      description: t('appModes.free.description'),
      primaryLabel: t('appModes.free.primary'),
      primaryPath: '/saves?type=free',
      secondaryLabel: t('appModes.free.secondary'),
      secondaryPath: '/saves?type=free',
    },
    {
      key: 'saves',
      title: t('appModes.saves.title'),
      tag: t('appModes.saves.tag'),
      icon: <PlayCircleOutlined />,
      description: t('appModes.saves.description'),
      primaryLabel: t('appModes.saves.primary'),
      primaryPath: '/saves',
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {modes.map((mode) => (
        <Col key={mode.key} xs={24} md={12} xl={compact ? 6 : 12}>
          <Card
            size={compact ? 'small' : 'default'}
            style={{ height: '100%' }}
            title={
              <Space>
                {mode.icon}
                <span>{mode.title}</span>
              </Space>
            }
            extra={<Tag>{mode.tag}</Tag>}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">{mode.description}</Text>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<FolderOpenOutlined />}
                  onClick={() => {
                    if (mode.primaryPath) {
                      navigate(mode.primaryPath);
                    }
                  }}
                >
                  {mode.primaryLabel}
                </Button>
                {mode.secondaryLabel && mode.secondaryPath ? (
                  <Button onClick={() => navigate(mode.secondaryPath)}>
                    {mode.secondaryLabel}
                  </Button>
                ) : null}
              </Space>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default AppModeCards;
